<template>
  <div class="precios w-100">

    <!-- Vistazo rápido: una credencial por nivel -->
    <section class="credenciales w-100" aria-label="Los tres niveles de acceso">
      <div class="marco px-xl-5 px-4">
        <div class="cred-intro">Tres niveles · uno por persona</div>
        <div class="cred-fila">
          <article v-for="n in niveles" :key="n.clave" :class="['cred', n.clave]">
            <div class="cred-banda"></div>
            <div class="cred-cab">
              <div class="cred-id">Credencial {{ n.id }}</div>
              <h2 class="cred-nom">{{ n.nombre }}</h2>
              <p class="cred-quien">{{ n.quien }}</p>
            </div>
            <div class="cred-precio">
              <span class="sig">$</span><span class="val">{{ n.precio }}</span><span class="per">/mes</span>
            </div>
            <div class="cred-lista">
              <div class="cred-l">Puede</div>
              <div v-for="(p, i) in n.puede" :key="i" :class="['cred-p', { sin: p.no }]">
                <span class="caja" aria-hidden="true">{{ p.no ? '' : '✓' }}</span>{{ p.texto }}
              </div>
            </div>
          </article>
        </div>
      </div>
    </section>

    <!-- El detalle: la misma información, abierta -->
    <section class="detalle w-100">
      <div class="marco px-xl-5 px-4">
        <h2 class="titulo-tabla">El detalle, punto por punto</h2>
        <p class="bajada-tabla">
          Lo mismo de arriba, abierto renglón por renglón. Si vas a comparar en serio, aquí está todo.
        </p>

        <div class="selnivel" role="group" aria-label="Elige el nivel que quieres ver">
          <button v-for="(n, i) in niveles" :key="n.clave" type="button"
                  :aria-pressed="String(columna === i + 1)"
                  @click="columna = i + 1">{{ n.nombre }}</button>
        </div>

        <div class="tabla-env">
          <table>
            <caption class="solo-lectores">
              Qué incluye cada nivel de acceso y su precio mensual por persona
            </caption>
            <thead>
              <tr>
                <th scope="col">Por persona, al mes <span class="lead">Precios en pesos, más IVA</span></th>
                <th v-for="(n, i) in niveles" :key="n.clave" scope="col" :class="{ oculta: escondida(i + 1) }">
                  {{ n.nombre }}
                  <span class="precio">${{ n.precio }}</span>
                  <span class="quien">{{ n.corto }}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              <template v-for="g in grupos" :key="g.titulo">
                <tr class="grupo"><td colspan="4">{{ g.titulo }}</td></tr>
                <tr v-for="f in g.filas" :key="f.que">
                  <td>{{ f.que }}<span v-if="f.apunte" class="apunte">{{ f.apunte }}</span></td>
                  <td v-for="(celda, i) in f.vale" :key="i"
                      :class="[claseCelda(celda), { oculta: escondida(i + 1) }]">{{ textoCelda(celda) }}</td>
                </tr>
              </template>
              <tr class="fila-tu">
                <td>Tu consultorio</td>
                <td colspan="3" class="cierra">La única fila que se calcula sola</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </section>

  </div>
</template>

<script>
/*
 * Tarifario publico de Onico. Direccion "Tabulador" con credenciales, elegida el 6-ago-2026.
 *
 * Dos bloques que hacen trabajos distintos y por eso no se sienten repetidos:
 *  - Las credenciales dan el vistazo: quien es cada nivel y que NO incluye (la linea tachada).
 *  - La tabla da el detalle para quien compara en serio.
 *
 * El cotizador vive en cotizador.vue y se monta justo despues desde el contenido de la
 * landing. La fila "Tu consultorio" es la costura entre los dos.
 */
export default {
  name: 'pricing',
  props: {
    content: { type: String, default: '' },
    con: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      columna: 1,    // en movil la tabla enseña un nivel a la vez
      esMovil: false, // va en data() para que sea reactivo: en created() no lo es

      niveles: [
        {
          clave: 'bajo', id: '01', nombre: 'Agenda', precio: '450',
          quien: 'Recepción y enfermería', corto: 'Recepción y enfermería',
          puede: [
            { texto: 'Agendar y reagendar citas' },
            { texto: 'Dar de alta pacientes' },
            { texto: 'Mandar recordatorios' },
            { texto: 'Abrir el expediente clínico', no: true },
          ],
        },
        {
          clave: 'medio', id: '02', nombre: 'Starter', precio: '600',
          quien: 'Consulta hasta 100 citas al mes', corto: 'Consulta moderada',
          puede: [
            { texto: 'Todo lo de Agenda' },
            { texto: 'Abrir el expediente clínico' },
            { texto: 'Recetar y firmar notas' },
            { texto: 'Pasar de 100 citas al mes', no: true },
          ],
        },
        {
          clave: 'alto', id: '03', nombre: 'Profesional', precio: '1,200',
          quien: 'Consulta sin límite', corto: 'Consulta sin límite',
          puede: [
            { texto: 'Todo lo de Starter' },
            { texto: 'Citas sin límite' },
            { texto: 'Marketing de recurrencia' },
            { texto: 'Citas desde tu sitio web' },
          ],
        },
      ],
      // true = incluido · false = no incluido · texto = dato
      grupos: [
        {
          titulo: 'Agenda y pacientes',
          filas: [
            { que: 'Agendar, reagendar y cancelar', vale: [true, true, true] },
            { que: 'Dar de alta y editar pacientes', vale: [true, true, true] },
            { que: 'Recordatorios por WhatsApp', vale: [true, true, true] },
            { que: 'Citas desde tu sitio web', vale: [true, true, true] },
          ],
        },
        {
          titulo: 'Expediente clínico',
          filas: [
            { que: 'Abrir y editar el expediente', apunte: 'Es lo que separa un nivel del otro', vale: [false, true, true] },
            { que: 'Recetas y notas firmadas', vale: [false, true, true] },
            { que: 'Historial y estudios del paciente', vale: [false, true, true] },
          ],
        },
        {
          titulo: 'Volumen de consulta',
          filas: [
            { que: 'Citas que puede atender al mes', vale: ['No atiende', '100', 'Sin límite'] },
            { que: 'Marketing de recurrencia', vale: [false, true, true] },
          ],
        },
      ],
    }
  },
  methods: {
    escondida(i) {
      return this.esMovil && this.columna !== i
    },
    claseCelda(c) {
      if (c === true) return 'si'
      if (c === false) return 'no'
      return c === 'Sin límite' ? 'dato si' : 'dato'
    },
    textoCelda(c) {
      if (c === true) return 'Sí'
      if (c === false) return 'No'
      return c
    },
    revisarAncho() {
      this.esMovil = window.matchMedia('(max-width: 760px)').matches
    },
  },
  mounted() {
    this.revisarAncho()
    window.addEventListener('resize', this.revisarAncho)
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.revisarAncho)
  },
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@400;500;600;700&family=IBM+Plex+Sans:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500;600&display=swap');

.precios{
  --tinta:#1a1a1a; --tinta2:#5c5c5c; --tinta3:#8a8a8a;
  --linea:#ececec; --coral:#ff5660; --coral-hondo:#e54553;
  --coral-piso:#fff6f6; --blanco:#fff;
  font-family:'IBM Plex Sans',system-ui,sans-serif;
  color:var(--tinta); font-size:16px; line-height:1.55;
  text-align:left; /* la landing centra por herencia; el Tabulador va a la izquierda */
}
.marco{max-width:1080px;margin:0 auto}
.solo-lectores{position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0)}
.precios :focus-visible{outline:2px solid var(--coral);outline-offset:3px;border-radius:3px}

/* ── Credenciales ── */
.credenciales{background:#eef1f3;padding:52px 0 56px}
.cred-intro{
  font-family:'IBM Plex Mono',monospace;font-size:.72rem;font-weight:500;
  letter-spacing:.22em;text-transform:uppercase;color:#7d8b94;margin-bottom:22px;
}
.cred-fila{display:grid;grid-template-columns:repeat(3,1fr);gap:18px}
.cred{
  background:var(--blanco);border-radius:14px;overflow:hidden;display:flex;flex-direction:column;
  box-shadow:0 1px 2px rgba(18,24,28,.05),0 10px 26px rgba(18,24,28,.07);
}
.cred-banda{height:8px;background:#c8d2d8}
.cred.medio .cred-banda{background:var(--coral-hondo)}
.cred.alto .cred-banda{background:var(--coral)}
.cred-cab{padding:22px 24px 18px;border-bottom:1px dashed #dde3e7}
.cred-id{
  font-family:'IBM Plex Mono',monospace;font-size:.68rem;font-weight:500;
  letter-spacing:.19em;text-transform:uppercase;color:#93a0a8;
}
.cred-nom{
  font-family:'Barlow Condensed',sans-serif;font-weight:700;font-size:2rem;
  text-transform:uppercase;letter-spacing:.03em;line-height:1;margin:7px 0 5px;color:var(--tinta);
}
.cred-quien{font-size:.92rem;color:var(--tinta2);margin:0}
.cred-precio{padding:20px 24px 16px;display:flex;align-items:baseline;gap:7px}
.cred-precio .sig{font-family:'IBM Plex Mono',monospace;font-size:1.15rem;color:#93a0a8}
.cred-precio .val{
  font-family:'IBM Plex Mono',monospace;font-weight:600;font-size:2.7rem;
  letter-spacing:-.045em;line-height:1;
}
.cred-precio .per{font-family:'IBM Plex Mono',monospace;font-size:.8rem;color:#93a0a8}
.cred-lista{padding:0 24px 26px}
.cred-l{
  font-family:'IBM Plex Mono',monospace;font-size:.66rem;font-weight:500;
  letter-spacing:.19em;text-transform:uppercase;color:#a3aeb4;margin-bottom:12px;
}
.cred-p{display:flex;align-items:flex-start;gap:11px;padding:6px 0;font-size:.98rem;color:#33403f}
.cred-p .caja{
  width:20px;height:20px;border-radius:5px;flex-shrink:0;margin-top:1px;
  background:var(--coral);color:var(--blanco);
  display:grid;place-items:center;font-size:.72rem;font-weight:700;line-height:1;
}
.cred-p.sin{color:#a3aeb4;text-decoration:line-through;text-decoration-color:#c8d2d8}
.cred-p.sin .caja{background:#e2e8eb}

/* ── Detalle ── */
.detalle{background:var(--blanco);padding:54px 0 0}
.titulo-tabla{
  font-family:'Barlow Condensed',sans-serif;font-weight:700;
  font-size:clamp(1.8rem,4vw,2.5rem);line-height:1;
  text-transform:uppercase;letter-spacing:.03em;color:var(--tinta);
}
.bajada-tabla{margin:10px 0 0;font-size:1rem;color:var(--tinta2);max-width:50ch}

.selnivel{display:none;gap:8px;margin:26px 0 4px;flex-wrap:wrap}
.selnivel button{
  font-family:'Barlow Condensed',sans-serif;font-weight:600;font-size:1rem;
  text-transform:uppercase;letter-spacing:.06em;
  background:none;border:1px solid var(--linea);border-radius:999px;
  padding:7px 15px;cursor:pointer;color:var(--tinta2);
}
.selnivel button[aria-pressed="true"]{background:var(--tinta);border-color:var(--tinta);color:var(--blanco)}

.tabla-env{margin-top:22px}
table{width:100%;border-collapse:collapse}
thead th{
  font-family:'Barlow Condensed',sans-serif;font-weight:600;
  font-size:1.3rem;text-transform:uppercase;letter-spacing:.05em;
  text-align:right;padding:0 0 12px 18px;border-bottom:2px solid var(--tinta);
  vertical-align:bottom;color:var(--tinta);
}
thead th:first-child{text-align:left;padding-left:0;width:40%}
thead th .precio{
  display:block;font-family:'IBM Plex Mono',monospace;font-size:1.55rem;
  font-weight:600;color:var(--coral);letter-spacing:-.03em;margin-top:4px;
}
thead th .quien,thead th .lead{
  display:block;font-family:'IBM Plex Sans',sans-serif;font-size:.78rem;
  font-weight:400;text-transform:none;letter-spacing:0;color:var(--tinta3);margin-top:2px;
}
tbody td{
  padding:11px 0 11px 18px;border-bottom:1px solid var(--linea);
  font-size:.95rem;text-align:right;color:var(--tinta2);
}
tbody td:first-child{text-align:left;padding-left:0;color:var(--tinta)}
tr.grupo td{
  font-family:'Barlow Condensed',sans-serif;font-weight:600;font-size:.95rem;
  text-transform:uppercase;letter-spacing:.15em;color:var(--tinta3);
  padding-top:30px;padding-bottom:7px;border-bottom:1px solid var(--tinta);
}
.si{color:var(--coral);font-weight:600}
.no{color:#cfcfcf}
.dato{font-family:'IBM Plex Mono',monospace;font-size:.9rem}
td .apunte{display:block;font-size:.78rem;color:var(--tinta3);margin-top:1px}

/* Costura con el cotizador: esta fila abre el bloque coral que sigue */
tr.fila-tu td{
  border-bottom:none;padding-top:20px;padding-bottom:20px;background:var(--coral-piso);
}
tr.fila-tu td:first-child{
  font-family:'Barlow Condensed',sans-serif;font-weight:700;
  font-size:1.3rem;text-transform:uppercase;letter-spacing:.05em;color:var(--tinta);
  padding-left:18px;
}
tr.fila-tu td.cierra{padding-right:18px;font-size:.9rem}

@media (max-width:980px){
  .cred-fila{grid-template-columns:1fr;gap:14px}
  .cred{display:grid;grid-template-columns:auto 1fr;
        grid-template-areas:'banda banda' 'cab precio' 'lista lista'}
  .cred-banda{grid-area:banda}
  .cred-cab{grid-area:cab;border-bottom:none}
  .cred-precio{grid-area:precio;justify-content:flex-end;align-items:center;padding-right:24px}
  .cred-lista{grid-area:lista;border-top:1px dashed #dde3e7;padding-top:18px;margin-top:2px}
}
@media (max-width:760px){
  .credenciales{padding:38px 0 42px}
  .detalle{padding-top:40px}
  .cred-nom{font-size:1.7rem}
  .cred-precio .val{font-size:2.2rem}
  .selnivel{display:flex}
  thead th{font-size:1.05rem;padding-left:12px}
  thead th .precio{font-size:1.25rem}
  tbody td{padding-left:12px;font-size:.9rem}
  .oculta{display:none}
}
</style>
